import type {
  HeatmapNode,
  HotTopicCard,
  MarketIndex,
  MarketIndexPoint,
  MarketRecap,
  NewsCategoryOption,
  PreviewNewsCard,
  SelectedCategory,
} from "../types";
import { isoDate, sleep } from "../utils/format";

function seededSeries(range: MarketIndex["range"]): MarketIndexPoint[] {
  // 1:1 观感：1D 用日内波动；其他范围做平滑扩展
  const n = range === "1D" ? 80 : range === "5D" ? 60 : 70;
  const base = 6920;
  const drift = range === "1D" ? 0.55 : 0.22;

  let v1 = base;
  let v2 = 188;
  let v3 = 450;
  const out: MarketIndexPoint[] = [];

  for (let i = 0; i < n; i++) {
    const noise = Math.sin(i / 6) * 2 + Math.cos(i / 13) * 1.2;
    v1 += drift + noise * 0.35;
    v2 += drift * 0.02 + Math.sin(i / 5) * 0.08 + (i % 9 === 0 ? -0.25 : 0.12);
    v3 += drift * 0.06 + Math.cos(i / 7) * 0.25 + (i % 12 === 0 ? -0.9 : 0.55);

    const label =
      range === "1D"
        ? i < 20
          ? "10:00 AM"
          : i < 40
          ? "11:00 AM"
          : i < 60
          ? "1:00 PM"
          : "3:00 PM"
        : `P${i + 1}`;

    out.push({
      t: label,
      v1: v1,
      v2: v2,
      v3: v3,
    });
  }

  return out;
}

function mockIndex(range: MarketIndex["range"]): MarketIndex {
  const series = seededSeries(range);
  const last = series[series.length - 1]?.v1 ?? 6944.82;
  const value = Math.round(last * 100) / 100;

  return {
    symbol: "SPX",
    name: "S&P 500",
    value: value,
    changeAbs: 43.47,
    changePct: 3.62,
    asOf: "Jan 6 4:58:53 PM UTC-5",
    disclaimer: "INDEXSP, Disclaimer",
    range,
    series,
    compareRows: [
      {
        symbol: "SPX",
        name: "S&P 500",
        price: 6945.52,
        changeAbs: 43.47,
        changePct: 3.62,
        tone: "up",
        colorDot: "cyan",
      },
      {
        symbol: "NVDA",
        name: "NVIDIA Corp",
        price: 187.29,
        changeAbs: -0.84,
        changePct: -1.62,
        tone: "down",
        colorDot: "red",
      },
      {
        symbol: "TSLA",
        name: "Tesla Inc",
        price: 432.96,
        changeAbs: -18.71,
        changePct: -4.62,
        tone: "down",
        colorDot: "orange",
      },
    ],
  };
}

function mockHeatmapTree(): HeatmapNode {
  // Treemap: 头部大票 + 大量小票，观感接近截图
  const big = [
    { name: "MSFT", value: 16, changePct: 1.04 },
    { name: "NVDA", value: 14, changePct: 1.08 },
    { name: "AAPL", value: 15, changePct: -0.77 },
    { name: "GOOG", value: 12, changePct: 2.05 },
    { name: "AMZN", value: 10, changePct: 0.68 },
    { name: "TSLA", value: 9, changePct: 1.21 },
    { name: "LLY", value: 8, changePct: 2.74 },
    { name: "JPM", value: 7, changePct: -0.92 },
    { name: "BRK.B", value: 7, changePct: -0.44 },
  ];

  const smallTickers = [
    "META",
    "AVGO",
    "ORCL",
    "PLTR",
    "AMD",
    "NFLX",
    "COST",
    "XOM",
    "CVX",
    "PG",
    "KO",
    "PEP",
    "WMT",
    "DIS",
    "ADBE",
    "INTC",
    "CRM",
    "QCOM",
    "TXN",
    "GE",
    "BA",
    "CSCO",
    "NKE",
    "PFE",
    "MRK",
    "UNH",
    "ABBV",
    "TMO",
    "CAT",
    "DE",
    "GS",
    "MS",
    "V",
    "MA",
    "AXP",
    "HD",
    "LOW",
    "MCD",
    "SBUX",
    "CMG",
    "AMAT",
    "LRCX",
    "MU",
    "BKNG",
    "UBER",
    "SHOP",
    "SNOW",
    "NOW",
    "PANW",
    "CRWD",
    "SNPS",
    "CDNS",
    "GILD",
    "JNJ",
    "CVS",
    "C",
    "BAC",
    "WFC",
    "T",
    "VZ",
    "TMUS",
    "NEE",
    "DUK",
    "SO",
    "COP",
    "SLB",
  ];

  const small = smallTickers.map((t, i) => {
    const v = 1 + (i % 5) * 0.25;
    const change = Math.sin(i / 4) * 1.6 + (i % 9 === 0 ? -2.2 : 0.3);
    return { name: t, value: v, changePct: Math.round(change * 100) / 100 };
  });

  return {
    name: "S&P 500",
    value: 0,
    children: [
      {
        name: "Technology",
        value: 0,
        children: [...big.slice(0, 6), ...small.slice(0, 24)],
      },
      {
        name: "Healthcare",
        value: 0,
        children: [...big.slice(6, 7), ...small.slice(24, 42)],
      },
      {
        name: "Financials",
        value: 0,
        children: [...big.slice(7, 9), ...small.slice(42, 56)],
      },
      {
        name: "Energy & Utilities",
        value: 0,
        children: [...small.slice(56)],
      },
    ],
  };
}

export const mockApi = {
  async getHotTopics(date = isoDate()): Promise<{ items: HotTopicCard[] }> {
    await sleep(260);
    void date;
    return {
      items: [
        {
          id: "nvidia-ai-ces",
          tag: { label: "AI", tone: "green" },
          title:
            "Nvidia’s Jensen Huang unveils next-gen AI platforms and ‘physical AI’ vision at CES 2026",
          body: "At CES 2026 in Las Vegas, Nvidia CEO Jensen Huang delivered a major keynote introducing the fully production-ready Vera Rubin AI computing platform and an array of open AI models targeting robotics, autonomous driving, and real-world AI applications. Huang described the rise of ‘physical AI’ as a transformative moment akin to a ‘ChatGPT moment,’ signaling AI’s evolution beyond virtual tasks into machines that interact with the physical world. He also highlighted partnerships for deployment and emphasized Nvidia’s leadership in scaling AI infrastructure globally.",
          bearish:
            "AMD, Alphabet/Google, Broadcom, competing AI hardware and networking offerings that could pressure Nvidia’s market share.",
          bullish:
            "Nvidia (NVDA), partners integrating Rubin/Almayo platforms such as Mercedes-Benz autonomous systems and cloud AI adopters.",
          sources: "NVIDIA Blog",
        },
        {
          id: "nike-focus",
          tag: { label: "Public", tone: "blue" },
          title:
            "Nike’s focus on core footwear shows early gains but faces ongoing pressure",
          body: "Nike’s North American training, running, and basketball shoes returned to double-digit sales growth, and inventory fell 2% YoY, boosting partner confidence. However, higher tariffs added ~1.5B in costs, and heavy investments may drag 2026 profit growth. In China, fierce local competition and slow direct-to-consumer recovery mean FY2026 DTC growth remains challenging.",
          bearish:
            "International brands with weak China operations (Nike NKE, Adidas ADDYY) and high-inventory sneaker models.",
          bullish:
            "Foot Locker (FL), Dick’s Sporting Goods (DKS) and other athletic footwear retailers.",
          sources: "Reuters",
        },
        {
          id: "preview-card-seed",
          title: "—",
          body: "",
        },
      ],
    };
  },

  async getCategories(): Promise<{ categories: NewsCategoryOption[] }> {
    await sleep(180);
    return {
      categories: [
        { key: "finance", label: "finance" },
        { key: "energy", label: "energy" },
        { key: "top", label: "Top News" },
        { key: "tech", label: "technology" },
        { key: "macro", label: "macro" },
        { key: "crypto", label: "crypto" },
      ],
    };
  },

  async previewNews(
    selections: SelectedCategory[]
  ): Promise<{ card: PreviewNewsCard }> {
    await sleep(340);

    // 根据 selections 轻微变化文案（mock）
    const keys = selections.map((s) => s.key).join(",");
    const financeBias = keys.includes("finance");
    const techBias = keys.includes("tech");

    return {
      card: {
        paragraph:
          "Its Artificial Intelligence Platform (AIP) seen as a key catalyst. Revenue growth is accelerating year-over-year, with a rising share converting to operating profit margin, indicating stronger profitability and scalability.",
        bearish: financeBias
          ? "Palantir (PLTR)’s elevated valuation premium after the >120% rally increases downside risk if growth decelerates; rivals such as C3.ai (AI) and IBM (IBM) may outpace it if PLTR growth falters."
          : "Higher valuation multiple raises sensitivity to guidance; competitive pressure and contract concentration could amplify drawdowns.",
        bullish: techBias
          ? "Palantir (PLTR) benefiting from GenAI adoption and the AIP launch; Microsoft (MSFT) and Google (GOOGL) as ecosystem partners could accelerate enterprise AI deployments."
          : "Enterprise AI spend rotation and improved operating leverage may sustain upside if execution remains consistent.",
        sources: "AAStocks Financial News",
      },
    };
  },

  async analyzeQuery(
    query: string
  ): Promise<{ answer: string; followups: string[] }> {
    await sleep(380);
    const q = query.trim();
    if (!q) {
      return {
        answer: "请输入你想分析的新闻、公司或宏观主题。",
        followups: [
          "NVDA 本周驱动因素？",
          "美债收益率对科技股的影响？",
          "如何解读能源板块轮动？",
        ],
      };
    }
    return {
      answer: `已收到：${q}。\n\n（Mock）系统会基于所选新闻源与资产池，输出：1) 事件摘要；2) 影响路径（行业/主题/因子）；3) 多空要点；4) 风险项与观察指标。`,
      followups: ["给出多空分歧点", "补充相关标的", "生成一段可复用的研究摘要"],
    };
  },

  async getMarketIndex(
    range: MarketIndex["range"]
  ): Promise<{ index: MarketIndex }> {
    await sleep(240);
    return { index: mockIndex(range) };
  },

  async getHeatmap(): Promise<{ tree: HeatmapNode }> {
    await sleep(280);
    return { tree: mockHeatmapTree() };
  },

  async getMarketRecap(): Promise<{ recap: MarketRecap }> {
    await sleep(220);
    return {
      recap: {
        sector: [
          { label: "Healthcare", changePct: 1.96 },
          { label: "Energy", changePct: -2.81 },
        ],
        theme: [
          { label: "Memory Chips", changePct: 27.6 },
          { label: "AI Chips", changePct: -0.5 },
        ],
        narrative:
          "The U.S. stock market closed higher today as major indexes advanced with broad participation and strong sector dynamics supporting the rally. Healthcare stocks emerged as the best-performing sector, driven by gains in biotech and pharmaceutical names, while the Energy sector lagged on weaker commodity sentiment and profit-taking after recent strength. Thematic drivers showed strength in data storage and memory chip stocks, boosted by positive AI and cloud demand outlooks, while shares of AI hardware names saw slight weakness due to profit taking and mixed short-term sentiment. Market breadth improved as more stocks rose than fell on both the NYSE and Nasdaq, reflecting continued investor risk appetite, although macroeconomic indicators and monetary policy expectations remain key near-term influences. Looking ahead, investors remain focused on upcoming earnings reports, economic data, and central bank signals, with sector rotation into defensive and growth themes suggesting selective leadership even as cyclical pressures persist.",
      },
    };
  },
};
